import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import Button from 'src/components/ui/Button/Button';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import { addModal } from 'src/features/globalSlice';
import {
  getCompanyDetails,
  removeCompany,
  setCompanyDetails,
} from 'src/store/actions/companyActions';
import { Company } from 'src/store/models/Company';
import { ModalType } from 'src/store/models/Modal';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';

import classes from './CompanyDetails.module.scss';
import CompanyDetailsForm from './CompanyDetailsForm/CompanyDetailsForm';

const defaultValues: Company = {
  title: '',
  line1: '',
  line2: '',
  zip: '',
  city: '',
  country: '',
  eMail: '',
  phone: '',
  notes: '',
};

export interface CompanyDetailsDetailsProps {
  edit?: boolean;
}

const CompanyDetails: FC<CompanyDetailsDetailsProps> = ({ edit = false }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const params = useParams();

  const methods = useForm<Company>({
    mode: 'onChange',
    defaultValues,
  });

  const loading = useLoadingSelector({
    actionType: getCompanyDetails.toString(),
  });
  const companyDetails = useAppSelector(
    (state) => state.company.companyDetails
  );

  useEffect(() => {
    dispatch(setCompanyDetails(null));
    if (edit) {
      dispatch(getCompanyDetails({ companyId: params?.companyId }));
    }
    return () => {
      dispatch(setCompanyDetails(null));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (companyDetails) {
      methods?.reset(companyDetails);
    }
    // eslint-disable-next-line
  }, [companyDetails]);

  const removeCompanyHandler = () => {
    dispatch(
      addModal({
        title: t('common:are_you_sure'),
        modalType: ModalType.REMOVE_COMPANY,
        loadingAction: removeCompany.type,
        data: methods?.getValues(),
      })
    );
  };

  return (
    <FormProvider {...methods}>
      <div className={classes.Container}>
        <PageHeader
          title={
            edit
              ? t('service_account:edit_company')
              : t('service_account:add_new_company')
          }
          showBackButton
          backConfirmationText={
            methods?.formState.isDirty && t('common:changes_not_saved')
          }
          endNode={
            <Button
              type="submit"
              form="new-company-form"
              disabled={
                !(methods?.formState?.isValid && methods.formState.isDirty) ||
                loading
              }
              className={classes.HeaderButton}
            >
              {edit ? t('common:save_changes') : t('products:add_now')}
            </Button>
          }
        />
        <CompanyDetailsForm edit={edit} loading={loading} />
        {edit && !loading && (
          <Button onClick={removeCompanyHandler} variant="outlined">
            {t('service_account:remove_company')}
          </Button>
        )}
      </div>
    </FormProvider>
  );
};

export default CompanyDetails;
